"use client";

import { DateStorefrontInput } from "@frontend/components/input/DateStorefrontInput";
import { motion } from "framer-motion";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@frontend/shared/components/ui/form";
import { zodResolver } from "@hookform/resolvers/zod";
import { ArrowLeft } from "lucide-react";
import { useTranslations } from "@frontend/lib/next-intl-typed";
import { useForm } from "react-hook-form";
import {
  useInsuranceConnectorContext,
  useInsuranceConnectorSend,
} from "../insurance-connector-provider";
import {
  type BirthdayFormData,
  birthdaySchema,
} from "../machine/schemas/validation.schemas";

export function EligibilityBirthdayInput() {
  const t = useTranslations("InsuranceConnector");
  const context = useInsuranceConnectorContext();
  const send = useInsuranceConnectorSend();

  const form = useForm<BirthdayFormData>({
    resolver: zodResolver(birthdaySchema),
    defaultValues: {
      birthday: context.birthday || "",
    },
  });

  const onSubmit = (data: BirthdayFormData) => {
    send({ type: "SUBMIT", data });
  };

  return (
    <div className="h-full flex flex-col justify-between pt-6 md:pt-[48px]">
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className="gap-4 flex flex-col h-full justify-between"
        >
          <div className="flex flex-col gap-4 md:gap-12">
            <h2 className="text-2xl md:text-[32px] leading-[120%] tracking-[-0.02em] font-semibold">
              {t("WhenWereYouBorn")}
            </h2>
            <FormField
              control={form.control}
              name="birthday"
              render={({ field }) => (
                <FormItem className="w-full">
                  <FormLabel className="sr-only">{t("DateOfBirth")}</FormLabel>
                  <FormControl>
                    <DateStorefrontInput
                      className="rounded-full py-5 text-lg w-full px-5 border-transparent focus:border-transparent focus:ring-1 focus:ring-[#00A3FF] focus:outline-none focus-visible:ring-1 focus-visible:ring-[#00A3FF] focus-visible:ring-offset-0"
                      autoFocus
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className="flex gap-4 justify-end md:justify-between items-center pt-3 md:pt-0">
            <motion.button
              type="button"
              onClick={() => send({ type: "BACK" })}
              className="hidden md:flex border border-black rounded-full aspect-square w-[62px] h-[62px] items-center justify-center"
              aria-label={t("GoBack")}
              initial={{ backgroundColor: "transparent", color: "#000000" }}
              whileHover={{ backgroundColor: "#000000", color: "#ffffff" }}
              transition={{ duration: 0.3, ease: "easeInOut" }}
            >
              <ArrowLeft className="h-6 w-6" />
            </motion.button>
            <motion.button
              type="submit"
              className="inline-flex items-center justify-center rounded-full px-[53px] md:px-[48px] py-5 md:py-[20px] text-base md:text-xl font-medium text-white"
              initial={{ backgroundColor: "#000000" }}
              whileHover={{ backgroundColor: "#00A3FF" }}
              transition={{ duration: 0.3, ease: "easeInOut" }}
            >
              {t("Next")}
            </motion.button>
          </div>
        </form>
      </Form>
    </div>
  );
}
